import { logger } from '../utils/logger';
import axios from 'axios';
import { api, API_URL } from './api'; // Use normalized API_URL that always uses www.gotrutax.com

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1/nexus`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const nexusService = {
  /**
   * Get economic nexus insights for dashboard users
   * @param {number | null} companyId
   * @param {string | null} filingPeriod
   * @returns {Promise<any>}
   */
  getEconomicNexusInsights: async ({
    companyId = null,
    filingPeriod = null,
    rangeMode = 'calendar_year',
    calendarYear = new Date().getFullYear(),
  } = {}) => {
    const params = {
      range_mode: rangeMode,
      calendar_year: calendarYear,
    };
    if (companyId) params.company_id = companyId;
    if (filingPeriod) params.filing_period = filingPeriod;

    const response = await apiClient.get('/insights', { params });
    return response.data;
  },

  // Get nexus insights from real transaction data (NO HARDCODING)
  getEconomicNexusFromTransactions: async (companyId = 58) => {
    try {
      logger.log('Fetching real nexus insights for company:', companyId);
      
      // Get real nexus insights from the API (based on actual transactions in database)
      const insightsResponse = await apiClient.get('/insights', {
        params: { company_id: companyId }
      });
      
      if (insightsResponse.data && insightsResponse.data.length > 0) {
        logger.log('Retrieved real nexus insights:', insightsResponse.data.length, 'states');
        
        // Convert API insights to the format expected by the frontend
        return insightsResponse.data.map(insight => ({
          state_code: insight.state_code,
          state_name: insight.state_name,
          sales_threshold: insight.sales_threshold,
          current_sales: insight.current_sales,
          transaction_threshold: insight.transaction_threshold,
          current_transactions: insight.current_transactions,
          percent_to_sales_threshold: insight.percent_to_sales_threshold,
          percent_to_transaction_threshold: insight.percent_to_transaction_threshold,
          exceeded_threshold: insight.exceeded_threshold,
          approaching_threshold: insight.approaching_threshold,
          nexus_status: insight.nexus_status,
          rule_type: insight.threshold_type,
          effective_date: insight.effective_date,
          lookback_period: insight.lookback_period,
          current_period: insight.current_period,
          nexus_rule_triggered: insight.exceeded_threshold ? 
            nexusService.determineTriggeredRule(insight) : null,
          rule_explanation: nexusService.generateRuleExplanation(insight)
        }));
      }
      
      // If no insights available, return empty array (NO FALLBACK TO HARDCODED DATA)
      logger.warn('No nexus insights available for company', companyId);
      return [];
      
    } catch (error) {
      logger.error('Error fetching real nexus insights:', error);
      throw error; // Don't fallback to hardcoded data, let the error bubble up
    }
  },

  // Calculate nexus from real Shopify transaction data
  calculateNexusFromShopifyTransactions: async (transactions) => {
    // Aggregate sales by state from shipping addresses
    const stateAggregates = {};
    
    transactions.forEach(transaction => {
      const shippingAddress = transaction.shipping_address || transaction.billing_address;
      const state = shippingAddress?.state || shippingAddress?.province_code;
      
      if (state && transaction.amount && transaction.transaction_type === 'sale') {
        if (!stateAggregates[state]) {
          stateAggregates[state] = {
            total_sales: 0,
            transaction_count: 0,
            transactions: []
          };
        }
        
        stateAggregates[state].total_sales += parseFloat(transaction.amount);
        stateAggregates[state].transaction_count += 1;
        stateAggregates[state].transactions.push(transaction);
      }
    });
    
    // State nexus thresholds (2024 data)
    const stateThresholds = await nexusService.getStateThresholds();
    
    // Calculate nexus status for each state with activity
    const nexusInsights = Object.keys(stateAggregates).map(stateCode => {
      const stateData = stateAggregates[stateCode];
      const threshold = stateThresholds[stateCode];
      
      if (!threshold) return null;
      
      const salesProgress = (stateData.total_sales / threshold.sales) * 100;
      const transactionProgress = threshold.transactions ? 
        (stateData.transaction_count / threshold.transactions) * 100 : 0;
      
      // Determine which rule applies and why
      const ruleAnalysis = nexusService.analyzeNexusRules(
        stateData.total_sales, 
        stateData.transaction_count, 
        threshold
      );
      
      return {
        state_code: stateCode,
        state_name: nexusService.getStateName(stateCode),
        sales_threshold: threshold.sales,
        current_sales: Math.round(stateData.total_sales * 100) / 100,
        transaction_threshold: threshold.transactions,
        current_transactions: stateData.transaction_count,
        percent_to_sales_threshold: Math.round(salesProgress * 10) / 10,
        percent_to_transaction_threshold: threshold.transactions ? 
          Math.round(transactionProgress * 10) / 10 : null,
        exceeded_threshold: ruleAnalysis.exceeded,
        approaching_threshold: ruleAnalysis.approaching,
        nexus_status: ruleAnalysis.status,
        nexus_rule_triggered: ruleAnalysis.rule_triggered,
        rule_explanation: ruleAnalysis.explanation,
        rule_type: threshold.rule_type || 'OR',
        effective_date: threshold.effective_date || '2019-07-01',
        lookback_period: 'Last 12 months (real transaction data)',
        current_period: `${stateData.transaction_count} transactions totaling $${Math.round(stateData.total_sales).toLocaleString()}`
      };
    }).filter(Boolean);
    
    logger.log('Calculated nexus from real data:', nexusInsights);
    return nexusInsights;
  },

  // Generate enhanced sample data that looks realistic
  generateEnhancedSampleNexusData: async () => {
    logger.log('Generating enhanced sample nexus data based on realistic e-commerce patterns');
    
    // More realistic state data based on e-commerce patterns
    const realisticStateData = [
      // High-activity states (major e-commerce markets)
      { code: 'CA', sales: 487500, transactions: 1245, pattern: 'major_market' },
      { code: 'TX', sales: 234000, transactions: 567, pattern: 'major_market' },
      { code: 'NY', sales: 198000, transactions: 234, pattern: 'high_value_low_volume' },
      { code: 'FL', sales: 156000, transactions: 389, pattern: 'major_market' },
      
      // Exceeded states (triggered nexus)
      { code: 'OH', sales: 125300, transactions: 287, pattern: 'exceeded' },
      { code: 'AR', sales: 118700, transactions: 245, pattern: 'exceeded' },
      { code: 'IL', sales: 145600, transactions: 298, pattern: 'exceeded' },
      { code: 'GA', sales: 134200, transactions: 267, pattern: 'exceeded' },
      
      // Approaching states (90%+ of thresholds)
      { code: 'AZ', sales: 185000, transactions: 156, pattern: 'approaching' },
      { code: 'CO', sales: 94600, transactions: 187, pattern: 'approaching' },
      { code: 'WA', sales: 91200, transactions: 178, pattern: 'approaching' },
      { code: 'NC', sales: 92400, transactions: 185, pattern: 'approaching' },
      
      // Safe states (below thresholds)
      { code: 'MI', sales: 67800, transactions: 134, pattern: 'safe' },
      { code: 'PA', sales: 73400, transactions: 156, pattern: 'safe' },
      { code: 'VA', sales: 58900, transactions: 123, pattern: 'safe' },
      { code: 'WI', sales: 45600, transactions: 98, pattern: 'safe' },
      { code: 'MN', sales: 52300, transactions: 107, pattern: 'safe' },
    ];
    
    const stateThresholds = await nexusService.getStateThresholds();
    
    return realisticStateData.map(stateData => {
      const threshold = stateThresholds[stateData.code];
      if (!threshold) return null;
      
      const salesProgress = (stateData.sales / threshold.sales) * 100;
      const transactionProgress = threshold.transactions ? 
        (stateData.transactions / threshold.transactions) * 100 : 0;
      
      // Determine which rule applies and why
      const ruleAnalysis = nexusService.analyzeNexusRules(
        stateData.sales, 
        stateData.transactions, 
        threshold
      );
      
      return {
        state_code: stateData.code,
        state_name: nexusService.getStateName(stateData.code),
        sales_threshold: threshold.sales,
        current_sales: stateData.sales,
        transaction_threshold: threshold.transactions,
        current_transactions: stateData.transactions,
        percent_to_sales_threshold: Math.round(salesProgress * 10) / 10,
        percent_to_transaction_threshold: threshold.transactions ? 
          Math.round(transactionProgress * 10) / 10 : null,
        exceeded_threshold: ruleAnalysis.exceeded,
        approaching_threshold: ruleAnalysis.approaching,
        nexus_status: ruleAnalysis.status,
        nexus_rule_triggered: ruleAnalysis.rule_triggered,
        rule_explanation: ruleAnalysis.explanation,
        rule_type: threshold.rule_type || 'OR',
        effective_date: threshold.effective_date || '2019-07-01',
        lookback_period: 'Last 12 months (enhanced sample data)',
        current_period: `${stateData.transactions} sample transactions totaling $${stateData.sales.toLocaleString()}`
      };
    }).filter(Boolean);
  },

  // Analyze nexus rules and determine which rule triggered the status
  analyzeNexusRules: (currentSales, currentTransactions, threshold) => {
    const salesExceeded = currentSales >= threshold.sales;
    const transactionsExceeded = threshold.transactions ? 
      currentTransactions >= threshold.transactions : false;
    
    const salesProgress = (currentSales / threshold.sales) * 100;
    const transactionProgress = threshold.transactions ? 
      (currentTransactions / threshold.transactions) * 100 : 0;
    
    const salesApproaching = salesProgress >= 90 && salesProgress < 100;
    const transactionsApproaching = threshold.transactions ? 
      (transactionProgress >= 90 && transactionProgress < 100) : false;
    
    let exceeded = false;
    let approaching = false;
    let status = 'safe';
    let ruleTriggered = null;
    let explanation = '';
    
    // Determine rule type (OR vs AND)
    const ruleType = threshold.rule_type || 'OR';
    
    if (ruleType === 'AND') {
      // Both sales AND transactions must exceed thresholds
      exceeded = salesExceeded && (threshold.transactions ? transactionsExceeded : salesExceeded);
      approaching = (salesApproaching || transactionsApproaching) && !exceeded;
      
      if (exceeded) {
        status = 'exceeded';
        ruleTriggered = 'BOTH_THRESHOLDS';
        explanation = `Nexus established: Both sales ($${currentSales.toLocaleString()} ≥ $${threshold.sales.toLocaleString()}) AND transactions (${currentTransactions} ≥ ${threshold.transactions}) exceed thresholds.`;
      } else if (approaching) {
        status = 'approaching';
        if (salesApproaching && transactionsApproaching) {
          ruleTriggered = 'BOTH_APPROACHING';
          explanation = `Approaching nexus: Both sales (${salesProgress.toFixed(1)}%) and transactions (${transactionProgress.toFixed(1)}%) are near thresholds.`;
        } else if (salesApproaching) {
          ruleTriggered = 'SALES_APPROACHING';
          explanation = `Approaching nexus: Sales at ${salesProgress.toFixed(1)}% of $${threshold.sales.toLocaleString()} threshold.`;
        } else {
          ruleTriggered = 'TRANSACTIONS_APPROACHING';
          explanation = `Approaching nexus: Transactions at ${transactionProgress.toFixed(1)}% of ${threshold.transactions} threshold.`;
        }
      } else {
        status = 'safe';
        explanation = `No nexus: Both sales ($${currentSales.toLocaleString()}) and transactions (${currentTransactions}) below required thresholds.`;
      }
    } else {
      // Either sales OR transactions can trigger nexus (most common)
      exceeded = salesExceeded || transactionsExceeded;
      approaching = (salesApproaching || transactionsApproaching) && !exceeded;
      
      if (exceeded) {
        status = 'exceeded';
        if (salesExceeded && transactionsExceeded) {
          ruleTriggered = 'BOTH_THRESHOLDS';
          explanation = `Nexus established: Both sales ($${currentSales.toLocaleString()}) and transactions (${currentTransactions}) exceed thresholds.`;
        } else if (salesExceeded) {
          ruleTriggered = 'SALES_THRESHOLD';
          explanation = `Nexus established: Sales of $${currentSales.toLocaleString()} exceed the $${threshold.sales.toLocaleString()} threshold.`;
        } else {
          ruleTriggered = 'TRANSACTION_THRESHOLD';
          explanation = `Nexus established: ${currentTransactions} transactions exceed the ${threshold.transactions} transaction threshold.`;
        }
      } else if (approaching) {
        status = 'approaching';
        if (salesApproaching && transactionsApproaching) {
          ruleTriggered = 'BOTH_APPROACHING';
          explanation = `Approaching nexus: Sales at ${salesProgress.toFixed(1)}% and transactions at ${transactionProgress.toFixed(1)}% of thresholds.`;
        } else if (salesApproaching) {
          ruleTriggered = 'SALES_APPROACHING';
          explanation = `Approaching nexus: Sales at ${salesProgress.toFixed(1)}% of $${threshold.sales.toLocaleString()} threshold.`;
        } else {
          ruleTriggered = 'TRANSACTIONS_APPROACHING';
          explanation = `Approaching nexus: Transactions at ${transactionProgress.toFixed(1)}% of ${threshold.transactions} threshold.`;
        }
      } else {
        status = 'safe';
        if (threshold.transactions) {
          explanation = `No nexus: Sales ($${currentSales.toLocaleString()}) and transactions (${currentTransactions}) both below thresholds.`;
        } else {
          explanation = `No nexus: Sales of $${currentSales.toLocaleString()} below the $${threshold.sales.toLocaleString()} threshold.`;
        }
      }
    }
    
    return {
      exceeded,
      approaching,
      status,
      rule_triggered: ruleTriggered,
      explanation
    };
  },

  // Get state thresholds from API (replaces hardcoded version)
  getStateThresholds: async () => {
    try {
      const response = await apiClient.get('/thresholds');
      // Convert API response to the expected format
      const thresholds = {};
      response.data.forEach(rule => {
        const stateCode = rule.jurisdiction_code.split('-').pop(); // Extract state code from jurisdiction_code
        thresholds[stateCode] = {
          sales: rule.sales_threshold,
          transactions: rule.transaction_threshold,
          rule_type: rule.threshold_type,
          effective_date: rule.effective_date
        };
      });
      return thresholds;
    } catch (error) {
      logger.error('Error fetching state thresholds from API:', error);
      // Initialize nexus rules if they don't exist
      try {
        await nexusService.initializeNexusRules();
        logger.log('Initialized nexus rules, retrying...');
        const retryResponse = await apiClient.get('/thresholds');
        const thresholds = {};
        retryResponse.data.forEach(rule => {
          const stateCode = rule.jurisdiction_code.split('-').pop();
          thresholds[stateCode] = {
            sales: rule.sales_threshold,
            transactions: rule.transaction_threshold,
            rule_type: rule.threshold_type,
            effective_date: rule.effective_date
          };
        });
        return thresholds;
      } catch (initError) {
        logger.error('Failed to initialize nexus rules:', initError);
        throw error;
      }
    }
  },

  // Helper function to get state name from code
  getStateName: (code) => {
    const stateNames = {
      'AL': 'Alabama', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
      'CO': 'Colorado', 'CT': 'Connecticut', 'FL': 'Florida', 'GA': 'Georgia',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KY': 'Kentucky',
      'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts',
      'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
      'NE': 'Nebraska', 'NV': 'Nevada', 'NJ': 'New Jersey', 'NM': 'New Mexico',
      'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
      'OK': 'Oklahoma', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming'
    };
    return stateNames[code] || code;
  },

  // Helper function to determine which rule was triggered
  determineTriggeredRule: (insight) => {
    if (!insight.exceeded_threshold) return null;
    
    const salesExceeded = insight.percent_to_sales_threshold >= 100;
    const transactionsExceeded = insight.percent_to_transaction_threshold && 
                                insight.percent_to_transaction_threshold >= 100;
    
    if (insight.threshold_type === 'OR') {
      if (salesExceeded && transactionsExceeded) {
        return 'Both sales and transaction thresholds exceeded';
      } else if (salesExceeded) {
        return 'Sales threshold exceeded';
      } else if (transactionsExceeded) {
        return 'Transaction threshold exceeded';
      }
    } else if (insight.threshold_type === 'SALES_ONLY') {
      return 'Sales threshold exceeded';
    } else if (insight.threshold_type === 'AND') {
      return 'Both sales and transaction thresholds exceeded';
    }
    
    return 'Threshold exceeded';
  },

  // Helper function to generate rule explanation
  generateRuleExplanation: (insight) => {
    const { threshold_type, sales_threshold, transaction_threshold, 
            percent_to_sales_threshold, percent_to_transaction_threshold } = insight;
    
    let explanation = `${insight.state_name} nexus rule: `;
    
    if (threshold_type === 'SALES_ONLY') {
      explanation += `$${sales_threshold.toLocaleString()} in sales required. `;
      explanation += `Currently at ${percent_to_sales_threshold}% (${insight.current_sales.toLocaleString()}).`;
    } else if (threshold_type === 'OR') {
      explanation += `Either $${sales_threshold.toLocaleString()} in sales OR ${transaction_threshold} transactions required. `;
      explanation += `Currently at ${percent_to_sales_threshold}% sales and ${percent_to_transaction_threshold || 0}% transactions.`;
    } else if (threshold_type === 'AND') {
      explanation += `Both $${sales_threshold.toLocaleString()} in sales AND ${transaction_threshold} transactions required. `;
      explanation += `Currently at ${percent_to_sales_threshold}% sales and ${percent_to_transaction_threshold || 0}% transactions.`;
    }
    
    if (insight.exceeded_threshold) {
      explanation += ' ⚠️ NEXUS EXCEEDED - Registration required.';
    } else if (insight.approaching_threshold) {
      explanation += ' ⚡ Approaching threshold - Monitor closely.';
    }
    
    return explanation;
  },

  // Get nexus dashboard summary
  getDashboardSummary: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await apiClient.get('/dashboard-summary', { params });
    return response.data;
  },

  // Get current nexus status for all states
  getNexusStatus: async () => {
    const response = await apiClient.get('/status');
    return response.data;
  },

  // Get thresholds for all states from API
  getStateThresholdsFromAPI: async () => {
    const response = await apiClient.get('/thresholds');
    return response.data;
  },

  // Get detailed status for a specific state
  getStateStatus: async (stateCode) => {
    const response = await apiClient.get(`/states/${stateCode}`);
    return response.data;
  },

  // Update nexus settings for a state
  updateStateNexus: async (stateCode, nexusData) => {
    const response = await apiClient.put(`/states/${stateCode}`, nexusData);
    return response.data;
  },

  // Get historical sales by state
  getHistoricalSalesByState: async (stateCode, params) => {
    const response = await apiClient.get(`/history/${stateCode}`, { params });
    return response.data;
  },

  // Get nexus forecast based on current trends
  getNexusForecast: async () => {
    const response = await apiClient.get('/forecast');
    return response.data;
  },

  // Get notification settings for nexus alerts
  getNexusAlertSettings: async () => {
    const response = await apiClient.get('/alerts/settings');
    return response.data;
  },

  // Update notification settings for nexus alerts
  updateNexusAlertSettings: async (settingsData) => {
    const response = await apiClient.put('/alerts/settings', settingsData);
    return response.data;
  },

  // Get recent nexus alerts
  getRecentAlerts: async (params) => {
    const response = await apiClient.get('/alerts/recent', { params });
    return response.data;
  },

  // Get filing providers with optional filtering
  getFilingProviders: async (jurisdictionCodes = [], budget = null) => {
    const params = {};
    if (jurisdictionCodes.length > 0) {
      params.jurisdiction_codes = jurisdictionCodes;
    }
    if (budget) {
      params.budget = budget;
    }
    const response = await apiClient.get('/filing-providers', { params });
    return response.data;
  },

  // Initialize default nexus rules
  initializeNexusRules: async () => {
    const response = await apiClient.post('/rules/initialize');
    return response.data;
  },

  // Get detailed nexus status for a company
  getCompanyNexusStatus: async (companyId) => {
    const response = await apiClient.get(`/status/${companyId}`);
    return response.data;
  },

  getAllNexus: async () => {
    const response = await axios.get(`${API_URL}`);
    return response.data;
  },

  createNexus: async (nexusData) => {
    const response = await axios.post(`${API_URL}`, nexusData);
    return response.data;
  },

  updateNexus: async (id, nexusData) => {
    const response = await axios.put(`${API_URL}/${id}`, nexusData);
    return response.data;
  },

  deleteNexus: async (id) => {
    const response = await axios.delete(`${API_URL}/${id}`);
    return response.data;
  },

  // Utility function to check if nexus is exceeded for a jurisdiction
  checkNexusExceeded: (insight) => {
    return insight.exceeded_threshold;
  },

  // Utility function to check if approaching nexus threshold
  checkApproachingThreshold: (insight) => {
    return insight.approaching_threshold && !insight.exceeded_threshold;
  },

  // Get formatted threshold text
  getThresholdText: (insight) => {
    const { sales_threshold, transaction_threshold, threshold_type } = insight;
    
    if (threshold_type === 'SALES_ONLY') {
      return `$${sales_threshold.toLocaleString()} in sales`;
    } else if (threshold_type === 'TRANSACTIONS_ONLY') {
      return `${transaction_threshold} transactions`;
    } else if (threshold_type === 'EITHER') {
      return `$${sales_threshold.toLocaleString()} in sales OR ${transaction_threshold} transactions`;
    } else if (threshold_type === 'BOTH') {
      return `$${sales_threshold.toLocaleString()} in sales AND ${transaction_threshold} transactions`;
    }
    
    return 'Custom threshold';
  },

  // Get progress color based on percentage
  getProgressColor: (percentage) => {
    if (percentage >= 100) return 'error';
    if (percentage >= 90) return 'warning';
    if (percentage >= 75) return 'info';
    return 'success';
  },

  // Format currency values
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  // Calculate days until threshold at current rate
  calculateDaysToThreshold: (current, threshold, dailyAverage) => {
    if (dailyAverage <= 0 || current >= threshold) return null;
    const remaining = threshold - current;
    return Math.ceil(remaining / dailyAverage);
  },

  // Get compliance recommendations based on nexus status
  getComplianceRecommendations: (insights) => {
    const recommendations = [];
    
    const exceededStates = insights.filter(i => i.nexus_status === 'exceeded');
    const approachingStates = insights.filter(i => i.nexus_status === 'approaching');
    
    if (exceededStates.length > 0) {
      recommendations.push({
        type: 'critical',
        title: 'Register for Sales Tax',
        description: `You have nexus in ${exceededStates.length} state(s) and must register for sales tax collection.`,
        states: exceededStates.map(s => s.state_code),
        priority: 'high'
      });
    }
    
    if (approachingStates.length > 0) {
      recommendations.push({
        type: 'warning',
        title: 'Monitor Approaching Thresholds',
        description: `You are approaching nexus thresholds in ${approachingStates.length} state(s). Consider preparing for registration.`,
        states: approachingStates.map(s => s.state_code),
        priority: 'medium'
      });
    }
    
    if (insights.length > 10) {
      recommendations.push({
        type: 'info',
        title: 'Consider Tax Automation',
        description: 'With activity in multiple states, consider automated tax calculation and filing solutions.',
        priority: 'low'
      });
    }
    
    return recommendations;
  },

  // Calculate nexus status with detailed rule analysis
  calculateNexusFromTransactionInsights: (transactionData) => {
    logger.log('Calculating nexus from transaction data:', transactionData);
    
    // Enhanced state thresholds with detailed rule information
    const stateThresholds = {
      'AL': { sales: 250000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-01-01' },
      'AK': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2020-01-01' },
      'AZ': { sales: 200000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-01-01' },
      'AR': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-07-01' },
      'CA': { sales: 500000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-04-01' },
      'CO': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-06-01' },
      'CT': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-07-01' },
      'DE': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-01-01' },
      'FL': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2021-07-01' },
      'GA': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'HI': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-07-01' },
      'ID': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-06-01' },
      'IL': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'IN': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-10-01' },
      'IA': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'KS': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2021-07-01' },
      'KY': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-07-01' },
      'LA': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2020-01-01' },
      'ME': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-07-01' },
      'MD': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'MA': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-01-01' },
      'MI': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-10-01' },
      'MN': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-10-01' },
      'MS': { sales: 250000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2020-09-01' },
      'MO': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2023-01-01' },
      'MT': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2020-01-01' },
      'NE': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'NV': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-10-01' },
      'NH': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-07-01' },
      'NJ': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-11-01' },
      'NM': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-07-01' },
      'NY': { sales: 500000, transactions: 100, rule_type: 'OR', effective_date: '2019-06-01' },
      'NC': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-11-01' },
      'ND': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-07-01' },
      'OH': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'OK': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2018-07-01' },
      'OR': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2020-01-01' },
      'PA': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2018-03-01' },
      'RI': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-07-01' },
      'SC': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-11-01' },
      'SD': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-03-01' },
      'TN': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2020-07-01' },
      'TX': { sales: 500000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2019-10-01' },
      'UT': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'VT': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-07-01' },
      'VA': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-07-01' },
      'WA': { sales: 100000, transactions: null, rule_type: 'SALES_ONLY', effective_date: '2018-01-01' },
      'WV': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-01-01' },
      'WI': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2018-10-01' },
      'WY': { sales: 100000, transactions: 200, rule_type: 'OR', effective_date: '2019-02-01' }
    };

    // Process transaction data by state
    const stateData = {};
    
    if (transactionData && transactionData.length > 0) {
      transactionData.forEach(transaction => {
        const stateCode = transaction.shipping_address?.state || transaction.shipping_address?.province_code;
        if (!stateCode) return;

        if (!stateData[stateCode]) {
          stateData[stateCode] = {
            total_sales: 0,
            transaction_count: 0,
            transactions: []
          };
        }

        stateData[stateCode].total_sales += transaction.amount || 0;
        stateData[stateCode].transaction_count += 1;
        stateData[stateCode].transactions.push(transaction);
      });
    }

    // Calculate nexus status for each state with activity
    const nexusResults = [];
    
    Object.keys(stateData).forEach(stateCode => {
      const data = stateData[stateCode];
      const thresholds = stateThresholds[stateCode];
      
      if (!thresholds) return; // Skip if no threshold data

      const currentSales = data.total_sales;
      const currentTransactions = data.transaction_count;
      const salesThreshold = thresholds.sales;
      const transactionThreshold = thresholds.transactions;
      const ruleType = thresholds.rule_type;

      // Calculate percentages
      const salesPercentage = Math.round((currentSales / salesThreshold) * 100);
      const transactionPercentage = transactionThreshold ? 
        Math.round((currentTransactions / transactionThreshold) * 100) : 0;

      // Determine nexus status and rule triggered
      let nexusStatus = 'SAFE';
      let nexusRuleTriggered = 'NONE';
      let ruleExplanation = '';

      // Check sales threshold
      const salesExceeded = currentSales >= salesThreshold;
      const salesApproaching = currentSales >= (salesThreshold * 0.8);
      
      // Check transaction threshold (if exists)
      const transactionExceeded = transactionThreshold ? currentTransactions >= transactionThreshold : false;
      const transactionApproaching = transactionThreshold ? currentTransactions >= (transactionThreshold * 0.8) : false;

      // Determine status based on rule type
      if (ruleType === 'OR') {
        if (salesExceeded || transactionExceeded) {
          nexusStatus = 'EXCEEDED';
          if (salesExceeded && transactionExceeded) {
            nexusRuleTriggered = 'BOTH_THRESHOLDS';
            ruleExplanation = `Both sales ($${currentSales.toLocaleString()}) and transaction count (${currentTransactions}) exceed thresholds. Nexus applies under either condition.`;
          } else if (salesExceeded) {
            nexusRuleTriggered = 'SALES_THRESHOLD';
            ruleExplanation = `Sales volume of $${currentSales.toLocaleString()} exceeds the $${salesThreshold.toLocaleString()} threshold. Nexus applies regardless of transaction count.`;
          } else {
            nexusRuleTriggered = 'TRANSACTION_THRESHOLD';
            ruleExplanation = `Transaction count of ${currentTransactions} exceeds the ${transactionThreshold} threshold. Nexus applies regardless of sales volume.`;
          }
        } else if (salesApproaching || transactionApproaching) {
          nexusStatus = 'APPROACHING';
          if (salesApproaching && transactionApproaching) {
            nexusRuleTriggered = 'BOTH_APPROACHING';
            ruleExplanation = `Both sales (${salesPercentage}%) and transactions (${transactionPercentage}%) are approaching thresholds. Monitor closely to avoid unexpected nexus.`;
          } else if (salesApproaching) {
            nexusRuleTriggered = 'SALES_APPROACHING';
            ruleExplanation = `Sales volume at ${salesPercentage}% of $${salesThreshold.toLocaleString()} threshold. Approaching nexus creation.`;
          } else {
            nexusRuleTriggered = 'TRANSACTIONS_APPROACHING';
            ruleExplanation = `Transaction count at ${transactionPercentage}% of threshold (${currentTransactions} / ${transactionThreshold}). Approaching nexus creation.`;
          }
        } else {
          ruleExplanation = `Current activity ($${currentSales.toLocaleString()}, ${currentTransactions} transactions) is below both thresholds. Either sales >$${salesThreshold.toLocaleString()} OR transactions >${transactionThreshold} would create nexus.`;
        }
      } else if (ruleType === 'SALES_ONLY') {
        if (salesExceeded) {
          nexusStatus = 'EXCEEDED';
          nexusRuleTriggered = 'SALES_THRESHOLD';
          ruleExplanation = `Sales volume of $${currentSales.toLocaleString()} exceeds the $${salesThreshold.toLocaleString()} threshold. This state uses sales-only nexus rules.`;
        } else if (salesApproaching) {
          nexusStatus = 'APPROACHING';
          nexusRuleTriggered = 'SALES_APPROACHING';
          ruleExplanation = `Sales volume at ${salesPercentage}% of $${salesThreshold.toLocaleString()} threshold. This state only considers sales volume for nexus determination.`;
        } else {
          ruleExplanation = `Current sales ($${currentSales.toLocaleString()}) below $${salesThreshold.toLocaleString()} threshold. Only sales volume determines nexus in this state.`;
        }
      } else if (ruleType === 'AND') {
        if (salesExceeded && transactionExceeded) {
          nexusStatus = 'EXCEEDED';
          nexusRuleTriggered = 'BOTH_THRESHOLDS';
          ruleExplanation = `Both sales ($${currentSales.toLocaleString()}) AND transaction count (${currentTransactions}) exceed thresholds. This state requires both conditions.`;
        } else if (salesApproaching && transactionApproaching) {
          nexusStatus = 'APPROACHING';
          nexusRuleTriggered = 'BOTH_APPROACHING';
          ruleExplanation = `Both thresholds approaching: sales ${salesPercentage}%, transactions ${transactionPercentage}%. This state requires BOTH conditions to be met.`;
        } else {
          ruleExplanation = `Current activity below required thresholds. This state requires BOTH sales >$${salesThreshold.toLocaleString()} AND transactions >${transactionThreshold}.`;
        }
      }

      nexusResults.push({
        state_name: nexusService.getStateName(stateCode),
        state_code: stateCode,
        nexus_status: nexusStatus,
        current_sales: currentSales,
        sales_threshold: salesThreshold,
        percent_to_sales_threshold: salesPercentage,
        current_transactions: currentTransactions,
        transaction_threshold: transactionThreshold,
        percent_to_transaction_threshold: transactionPercentage,
        effective_date: thresholds.effective_date,
        lookback_period: '12 months',
        rule_type: ruleType,
        rule_explanation: ruleExplanation,
        nexus_rule_triggered: nexusRuleTriggered,
        current_period: `Data from last 12 months (${data.transaction_count} transactions analyzed)`
      });
    });

    return nexusResults.sort((a, b) => b.current_sales - a.current_sales);
  },

  /**
   * Get nexus thresholds for all jurisdictions
   * @returns {Promise} - Promise with nexus thresholds data
   */
  async getNexusThresholds() {
    try {
      const url = `${API_URL}/api/v1/nexus/thresholds`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error fetching nexus thresholds:', error);
      throw error;
    }
  },
  
  /**
   * Format nexus insights data for display
   * @param {object} apiResponse - API response from getEconomicNexusInsights
   * @returns {object} - Formatted data for UI components
   */
  formatNexusInsightsForDisplay(apiResponse) {
    if (!apiResponse || !apiResponse.insights) {
      return {
        nexusData: [],
        metadata: null,
        monthlyBreakdown: [],
        stateMonthlyData: {},
        availablePeriods: []
      };
    }
    
    // Format insights data
    const nexusData = apiResponse.insights.map(insight => ({
      stateCode: insight.state_code,
      stateName: insight.state_name,
      salesThreshold: insight.sales_threshold,
      currentSales: insight.current_sales,
      transactionThreshold: insight.transaction_threshold,
      currentTransactions: insight.current_transactions,
      percentToSalesThreshold: insight.percent_to_sales_threshold,
      percentToTransactionThreshold: insight.percent_to_transaction_threshold,
      exceededThreshold: insight.exceeded_threshold,
      approachingThreshold: insight.approaching_threshold,
      effectiveDate: insight.effective_date,
      nexusStatus: insight.nexus_status
    }));
    
    // Format monthly breakdown
    const monthlyBreakdown = apiResponse.monthly_breakdown || [];
    
    // Format state monthly data
    const stateMonthlyData = apiResponse.state_monthly_data || {};
    
    // Format available periods
    const availablePeriods = apiResponse.metadata?.available_periods || [];
    
    return {
      nexusData,
      metadata: apiResponse.metadata,
      monthlyBreakdown,
      stateMonthlyData,
      availablePeriods
    };
  },

  fetchNexusInsights: async (companyId) => {
    try {
      const response = await api.get(`/api/v1/nexus/insights?company_id=${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching nexus insights:', error);
      throw error;
    }
  },

  fetchNexusMonthlyBreakdown: async (companyId, filingPeriod = null) => {
    try {
      let url = `/nexus/monthly-breakdown?company_id=${companyId}`;
      if (filingPeriod) {
        url += `&filing_period=${filingPeriod}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      logger.error('Error fetching nexus monthly breakdown:', error);
      throw error;
    }
  },

  fetchNexusStatus: async (companyId) => {
    try {
      const response = await api.get(`/api/v1/nexus/status/${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching nexus status:', error);
      throw error;
    }
  },

  fetchNexusThresholds: async () => {
    try {
      const response = await api.get('/api/v1/nexus/thresholds');
      return response.data;
    } catch (error) {
      logger.error('Error fetching nexus thresholds:', error);
      throw error;
    }
  },

  fetchNexusDashboardSummary: async (
    companyId,
    { rangeMode = 'calendar_year', calendarYear = new Date().getFullYear() } = {}
  ) => {
    try {
      const response = await api.get(
        `/api/v1/nexus/dashboard-summary?company_id=${companyId}&range_mode=${rangeMode}&calendar_year=${calendarYear}`
      );
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Unexpected nexus summary response');
      }
      return response.data;
    } catch (error) {
      logger.error('Error fetching nexus dashboard summary:', error);
      throw error;
    }
  }
};

export default nexusService; 
